import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/client";
import "./ServiceCatalog.css";

const CATEGORIES = ["web_dev", "it_services", "language_services"];
const BLANK_SERVICE = {
    name: "",
    category: "web_dev",
    description: "",
    unit: "",
    default_price_cad: "",
    is_active: true,
};

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

const ServiceCatalog = () => {
    const { t } = useTranslation();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ ...BLANK_SERVICE });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [filterCat, setFilterCat] = useState("all");
    const [showNew, setShowNew] = useState(false);

    const loadServices = () => {
        setLoading(true);
        api.services()
            .then((r) => setServices(r.services || []))
            .finally(() => setLoading(false));
    };

    useEffect(loadServices, []);

    const startEdit = (svc) => {
        setEditingId(svc.id);
        setForm({ ...svc });
        setShowNew(false);
    };

    const startNew = () => {
        setForm({ ...BLANK_SERVICE });
        setEditingId(null);
        setShowNew(true);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setShowNew(false);
        setError("");
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((p) => ({
            ...p,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...form,
                default_price_cad: parseFloat(form.default_price_cad) || 0,
            };
            if (editingId) await api.updateService(editingId, payload);
            else await api.createService(payload);
            cancelEdit();
            loadServices();
        } catch (err) {
            setError(err.message || t("common.error"));
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivate = async (id) => {
        if (!window.confirm("Deactivate this service?")) return;
        try {
            await api.deleteService(id);
            loadServices();
        } catch (err) {
            setError(err.message);
        }
    };

    const displayed =
        filterCat === "all"
            ? services
            : services.filter((s) => s.category === filterCat);

    const grouped = CATEGORIES.reduce((acc, cat) => {
        const list = displayed.filter((s) => s.category === cat);
        if (list.length) acc[cat] = list;
        return acc;
    }, {});

    return (
        <div className="service-catalog">
            <div className="service-catalog__toolbar">
                <div className="section-header">
                    <h2>{t("services.title")}</h2>
                </div>
                <button className="btn btn--primary btn--sm" onClick={startNew}>
                    {t("services.new")}
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="service-catalog__filters">
                {["all", ...CATEGORIES].map((cat) => (
                    <button
                        key={cat}
                        className={`service-catalog__filter-btn ${filterCat === cat ? "service-catalog__filter-btn--active" : ""}`}
                        onClick={() => setFilterCat(cat)}
                    >
                        {cat === "all"
                            ? t("common.all")
                            : t(`services.categories.${cat}`)}
                    </button>
                ))}
            </div>

            {(showNew || editingId) && (
                <div className="service-catalog__form card">
                    <div className="section-header">
                        <h3>
                            {editingId ? t("services.edit") : t("services.new")}
                        </h3>
                    </div>
                    <form onSubmit={handleSave}>
                        <div className="service-catalog__form-grid">
                            <div className="form-group">
                                <label>{t("services.name")}</label>
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={handleFormChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>{t("services.category")}</label>
                                <select
                                    name="category"
                                    value={form.category}
                                    onChange={handleFormChange}
                                >
                                    {CATEGORIES.map((c) => (
                                        <option key={c} value={c}>
                                            {t(`services.categories.${c}`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t("services.unit")}</label>
                                <input
                                    name="unit"
                                    value={form.unit}
                                    onChange={handleFormChange}
                                    placeholder="hour, project, month…"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>{t("services.price")}</label>
                                <input
                                    type="number"
                                    name="default_price_cad"
                                    value={form.default_price_cad}
                                    onChange={handleFormChange}
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>{t("services.description")}</label>
                            <textarea
                                name="description"
                                value={form.description}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="service-catalog__form-actions">
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={cancelEdit}
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                type="submit"
                                className="btn btn--primary btn--sm"
                                disabled={saving}
                            >
                                {saving
                                    ? t("common.loading")
                                    : t("common.save")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <p style={{ color: "var(--color-text-muted)" }}>
                    {t("common.loading")}
                </p>
            ) : (
                Object.entries(grouped).map(([cat, list]) => (
                    <div key={cat} className="service-catalog__group">
                        <h3 className="service-catalog__group-title">
                            {t(`services.categories.${cat}`)}
                        </h3>
                        <table className="dash-table service-catalog__table">
                            <thead>
                                <tr>
                                    <th>{t("services.name")}</th>
                                    <th>{t("services.unit")}</th>
                                    <th className="dash-table__right">
                                        {t("services.price")}
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((svc) => (
                                    <tr key={svc.id}>
                                        <td>
                                            <div className="service-catalog__svc-name">
                                                {svc.name}
                                            </div>
                                            {svc.description && (
                                                <div className="service-catalog__svc-desc">
                                                    {svc.description}
                                                </div>
                                            )}
                                        </td>
                                        <td className="dash-table__muted">
                                            {svc.unit}
                                        </td>
                                        <td className="dash-table__right dash-table__mono">
                                            {formatCAD(svc.default_price_cad)}
                                        </td>
                                        <td>
                                            <div className="service-catalog__row-actions">
                                                <button
                                                    className="btn btn--ghost btn--sm"
                                                    onClick={() =>
                                                        startEdit(svc)
                                                    }
                                                >
                                                    {t("common.edit")}
                                                </button>
                                                <button
                                                    className="btn btn--danger btn--sm"
                                                    onClick={() =>
                                                        handleDeactivate(svc.id)
                                                    }
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))
            )}
        </div>
    );
};

export default ServiceCatalog;
